import { AxiosInstance } from 'axios'
import { StrapiMeta } from './types'

export interface LanguageItemDto {
  id: number
  name: string
  code: string
  isDefault: boolean
}

export type GetLanguagesDto = LanguageItemDto[]

const getLanguages = async (client: AxiosInstance): Promise<GetLanguagesDto> => {
  const path = '/i18n/locales'
  return (await client.get<GetLanguagesDto>(path)).data
}

export interface GetMenuArgs {
  locale: string
}

export interface GenericMenuItemDto {
  id: number
  Title: string
  Href: string
  Target_blank: boolean
  nested: unknown[]
}

export interface GetMenuDto {
  data: {
    id: number
    attributes: {
      createdAt: string
      updatedAt: string
      locale: string
      header: GenericMenuItemDto[]
      burger: GenericMenuItemDto[]
      footer: GenericMenuItemDto[]
      [key: string]: unknown
    }
  }
  meta: StrapiMeta
}

export const getMenu = async (client: AxiosInstance, { locale }: GetMenuArgs): Promise<GetMenuDto> => {
  const path = `/menu`
  const params = {
    locale: locale,
    sort: { createdAt: 'desc' },
    populate: {
      header: { populate: '*' },
      burger: { populate: '*' },
      footer: { populate: '*' },
    },
  }
  return (await client.get<GetMenuDto>(path, { params })).data
}

export interface GetSettingsArgs {
  locale: string
  fields?: string[]
}

export interface MapDto {
  id: number
  attributes: { [key: string]: unknown }
}

export interface GetSettingsDto {
  data: {
    id: number
    attributes: {
      phone: string
      email: string
      whatsapp: string
      instagram: string
      linkedin: string
      telegram: string
      youtube: string
      snapchat: string
      createdAt: string
      updatedAt: string
      locale: string
      google_play: string | null
      appstore: string | null
      map: string
      contact_me_button_text: string
      intearctive_map_button_text: string
      personal_data_link_text: string
    }
  }
  meta: StrapiMeta
}

const getSettings = async (
  client: AxiosInstance,
  { locale, fields = ['*'] }: GetSettingsArgs,
): Promise<GetSettingsDto> => {
  const path = `/setting`
  const params = {
    locale: locale,
    sort: { createdAt: 'desc' },
    populate: {
      fields,
      map: {
        populate: '*',
      },
    },
  }
  return (await client.get<GetSettingsDto>(path, { params })).data
}

export interface GetFooterMenuArgs {
  locale: string
}

export type CategoryDto = {
  type: 'category'
  id: number
  slug: string
  name: string
  show_in_footer: boolean
  order: number
  children: CategoryDto[]
}

const getFooterMenu = async (client: AxiosInstance, { locale }: GetFooterMenuArgs): Promise<CategoryDto[]> => {
  const path = '/menu/footer'
  const params = {
    locale,
    sort: { createdAt: 'desc' },
  }
  return (await client.get<CategoryDto[]>(path, { params })).data
}

export interface GetLanguageMenuSettingsArgs {
  locale: string
}

export type LanguageDto = LanguageItemDto

export interface MenuItemDto {
  id: number
  title: string
  url: string
  children?: MenuItemDto[]

  [key: string]: unknown
}

export interface MenuDataDto {
  header: MenuItemDto[]
  footer: MenuItemDto[]
  burger: MenuItemDto[]

  [key: string]: unknown
}

export interface GetLanguageMenuSettingsDto {
  languages: LanguageDto[]
  header: GenericMenuItemDto[]
  footer: GenericMenuItemDto[]
  footerMenu: CategoryDto[]
  burger: GenericMenuItemDto[]
  dataSettings: GetSettingsDto['data']['attributes']
}

export const getLanguageMenuSettings = async (
  client: AxiosInstance,
  { locale }: GetLanguageMenuSettingsArgs,
): Promise<GetLanguageMenuSettingsDto> => {
  const [languagesData, menuData, settingsData, footerMenu] = await Promise.all([
    getLanguages(client),
    getMenu(client, { locale }),
    getSettings(client, { locale }),
    getFooterMenu(client, { locale }),
  ])

  const menuAttributes = menuData.data.attributes

  return {
    languages: languagesData,
    header: menuAttributes.header,
    footer: menuAttributes.footer,
    burger: menuAttributes.burger,
    footerMenu,
    dataSettings: settingsData.data.attributes,
  }
}
